import React, { FC, useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import {
  getOrganizationIdConstraints,
  OrganizationBackupItem,
  OrganizationBackupStore,
  OrganizationItemStore,
  OrganizationSummaryStore,
} from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Row, App } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { useIdentity } from 'src/core/services/authentication';
import { InfoCircleOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import OrganizationImport from '../components/organization-import';
import { OrganizationExportImportContext, OrganizationExportImportContextAction, OrganizationExportState } from '../organization-export-context';
import { ENV } from 'src/';
import Moment from 'moment';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import UnauthorizedPage from 'src/pages/unauthorized';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';

const loadingIcon = <LoadingOutlined style={{ fontSize: 24, color: 'gray' }} spin />;
const notificationIcon = <InfoCircleOutlined style={{ fontSize: 24, color: '#4169E1' }} />;
const permissions = [PermissionsConstants.ManageAdminOrganization];

const OrganizationsBackupsPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const identity = useIdentity();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasManageAdminOrganitionsPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'createOn', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 50,
  } as Query);

  const organizationBackupStore = container.get(OrganizationBackupStore);
  const organizationBackupState = organizationBackupStore.state;

  const organizationStore = container.get(OrganizationSummaryStore);
  const organizationState = organizationStore.state;

  const organizationItemStore = container.get(OrganizationItemStore);

  const organizationIds = useRef<string[]>([]);

  const [organizationImportVisible, setOrganizationImportVisible] = useState(false);
  const { organizationExportState, organizationExportDispatch } = useContext(OrganizationExportImportContext);
  const [isImporting, setIsImporting] = useState(organizationExportState.importState.state === OrganizationExportState.Busy);
  const { notification, modal } = App.useApp();

  setHeader(t('OrganizationBackups'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organizations') },
    { path: 'organizations/backups', breadcrumbName: t('Backups') },
  ]);

  useEffect(
    () => {
      if (hasManageAdminOrganitionsPermission) load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const load = async () => {
    await organizationStore.load({ take: 1000000, skip: 0 });
    organizationIds.current = organizationState.items.value.map((v) => v.id);
    await loadBackupItems(query);
  };

  const loadBackupItems = async (query: Query) => {
    await organizationBackupStore.loadSelected(query, organizationIds.current);
    if (isImporting) {
      waitForImport();
    }
  };

  const waitForImport = async () => {
    await organizationExportState.importState.promise;
    setIsImporting(false);
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        filter: <DateRangeFilter />,
        field: 'createOn',
        title: t('CreatedOn'),
        width: '20%',
        renderer: (data) => <span>{Moment.utc(data.createOn).local().format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        sortable: true,
        searchable: true,
        field: 'OrganizationName',
        title: t('OrganizationName'),
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={`${organizationBackupStore.baseUrl}/azure-backups`}
            fieldName={'OrganizationName'}
            fieldValue={'organizationName'}
            extraQuery={getOrganizationIdConstraints(organizationIds.current)}
          />
        ),
        fiterField: 'OrganizationName',
        renderer: (data) => <span>{data.organizationName}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'OrganizationId',
        title: '',
        renderer: (data) => (
          <>
            {
              <Button
                type="dashed"
                className="solid-button"
                onClick={() => restoreBackup(data.backupId, data.organizationId)}
                style={{ marginRight: 10, marginBottom: 10 }}
              >
                {t('Restore')}
              </Button>
            }
            <Button type="dashed" className="solid-button" onClick={() => downloadBackup(data.backupId, data.organizationId)}>
              {t('Download')}
            </Button>
          </>
        ),
      },
    ],
    data: organizationBackupState.value,
  } as TableModel<OrganizationBackupItem>;

  const onUploadStart = async (promise: Promise<any>) => {
    setOrganizationImportVisible(false);
    setIsImporting(true);
    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateImportState,
      organizationId: '',
      promise: promise,
      state: OrganizationExportState.Busy,
    });

    notification.info({
      message: t('ImportingOrganizationStarted'),
      icon: notificationIcon,
      placement: 'bottomRight',
      key: 'importing-notification',
      duration: 0,
    });

    const r: { isSuccess: boolean; error?: string } = await promise;

    if (r.isSuccess) {
      notification.success({
        message: t('ImportingOrganizationSuccess'),
        placement: 'bottomRight',
        key: 'importing-notification',
      });
    } else {
      notification.error({
        message: config.environment === ENV.DEV ? r.error : t('ErrorImportingOrganization'),
        placement: 'bottomRight',
        key: 'importing-notification',
      });
    }

    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateImportState,
      organizationId: '',
      promise: promise,
      state: OrganizationExportState.Available,
    });
    loadBackupItems(query);
    setIsImporting(false);
  };

  const restoreBackup = async (backupId: string, organizationId: string) => {
    const organizationIsLocked = await organizationItemStore.checkLockedStatus(organizationId);

    if (organizationIsLocked) {
      notification.error({
        placement: 'bottomRight',
        duration: 20,
        style: {
          width: 600,
        },
        message: t('OrganizationIsLocked'),
      });
      return;
    }

    modal.confirm({
      title: t('RestoreBackupConfirmMessage'),
      cancelButtonProps: { type: 'primary' },
      onOk: async () => {
        const response = await organizationBackupStore.restoreOrganization(backupId, organizationId);
        if (response?.isSuccess) {
          notification.success({
            message: t('RestoreBackupSuccessMessage'),
            placement: 'bottomRight',
          });
        } else {
          notification.error({
            message: config.environment === ENV.DEV ? response?.error : t('ErrorRestoringOrganization'),
            placement: 'bottomRight',
          });
        }
        loadBackupItems(query);
      },
    });
  };

  const downloadBackup = async (backupId: string, organizationId: string) => {
    const response = await organizationBackupStore.generateDownloadUrl(backupId, organizationId);

    if (response.isSuccess) {
      const link = document.createElement('a');
      link.download = `${backupId}.eiox`;
      link.href = response.url;
      link.click();
    } else {
      notification.error({ placement: 'bottomRight', message: t('AnErrorOcurred') });
    }
  };

  if (!hasManageAdminOrganitionsPermission) return <UnauthorizedPage />;

  return (
    <>
      <Row align="top" justify="space-between">
        <Card className="card-table reorder-buttons" style={{ width: '100%' }}>
          <TableView
            rowKey="id"
            canDelete={false}
            canEdit={false}
            canSort
            pageSize={50}
            onQueryChanged={(query: Query) => {
              setQuery(query);
              loadBackupItems(query);
            }}
            onRefresh={() => loadBackupItems(query)}
            model={organizationBackupState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              organizationBackupState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(organizationBackupState.errorMessage.value) : t('OrganizationsTableError'))
            }
            rightToolbar={
              <>
                <li className="table-buttons-list-element" style={{ marginLeft: '10px', marginRight: 0 }}>
                  <Button
                    type="default"
                    onClick={() => setOrganizationImportVisible(true)}
                    icon={isImporting ? loadingIcon : <UploadOutlined />}
                    disabled={isImporting}
                  >
                    {isImporting ? t('Importing') : t('ImportOrganization')}
                  </Button>
                </li>
              </>
            }
          />
        </Card>
      </Row>
      {organizationImportVisible && <OrganizationImport onClose={() => setOrganizationImportVisible(false)} onUploadStart={onUploadStart} />}
    </>
  );
};

export default OrganizationsBackupsPage;
